import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { Fragment, useRef, useState } from "react";

// Props type definition, allowing for flexibility in the items' structure
type GenericComboboxProps<T extends { id: string; value: string }> = {
  items: T[];
  itemToString: (item: T) => string;
  onSelect: (item: T) => void;
  truncateOptions?: boolean;
  defaultValue?: T;
};

function GenericCombobox<T extends { id: string; value: string }>({
  items,
  itemToString,
  onSelect,
  defaultValue,
  truncateOptions = false,
}: GenericComboboxProps<T>) {
  const [selected, setSelected] = useState<T | null>(defaultValue ?? null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState("");

  const filteredItems =
    query === ""
      ? items
      : items.filter((item) =>
          itemToString(item).toLowerCase().replace(/\s+/g, "").includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const handleChange = (item: T) => {
    setSelected(item);
    onSelect(item);
  };

  return (
    <Combobox value={selected} onChange={handleChange}>
      {({ open }) => (
        <div className="relative w-full rounded-lg border border-gray-400">
          <div className="bg-popover text-popover-foreground relative w-full cursor-default overflow-hidden rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
            <Combobox.Input
              className="bg- w-full border-none bg-[#121c22] py-2 pl-3 pr-10 text-base leading-5 focus:outline-none focus-visible:ring-0"
              displayValue={(item: T) => (item ? itemToString(item) : "")}
              onChange={(event: any) => setQuery(event.target.value)}
              onClick={() => {
                if (!open) {
                  buttonRef.current?.click();
                }
              }}
              onFocus={() => {
                if (!open) {
                  buttonRef.current?.click();
                }
              }}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" ref={buttonRef}>
              <ChevronUpDownIcon className="h-5 w-5 text-base" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options className="bg-base-200 absolute z-[9] mt-1 max-h-60 w-full overflow-auto rounded-md py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              {filteredItems.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">Nothing found.</div>
              ) : (
                filteredItems.map((item) => (
                  <Combobox.Option
                    key={item.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 text-base ${
                        active ? "bg-teal-600 text-white" : ""
                      }`
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block ${truncateOptions ? "truncate" : ""} ${selected ? "font-medium" : "font-normal"}`}
                        >
                          {itemToString(item)}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-teal-600"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

export default GenericCombobox;
